// ============================================================================
// RESIZEOBSERVER FIX - MUST BE FIRST!
// This patches ResizeObserver to prevent the "loop completed with undelivered
// notifications" error from ever being thrown. This is the definitive fix.
// ============================================================================
(function () {
  // Store the original ResizeObserver
  const OriginalResizeObserver = window.ResizeObserver;

  // Create a patched version that wraps callbacks in requestAnimationFrame
  // This breaks the synchronous feedback loop that causes the error
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      // Wrap the callback to defer execution and break the loop
      const wrappedCallback = (entries, observer) => {
        // Use requestAnimationFrame to defer the callback
        // This prevents the synchronous resize -> callback -> resize loop
        window.requestAnimationFrame(() => {
          // Only call if the observer is still connected
          if (this._connected !== false) {
            callback(entries, observer);
          }
        });
      };
      super(wrappedCallback);
      this._connected = true;
    }

    disconnect() {
      this._connected = false;
      super.disconnect();
    }
  };

  // Preserve static properties if any exist
  Object.keys(OriginalResizeObserver).forEach((key) => {
    window.ResizeObserver[key] = OriginalResizeObserver[key];
  });
})();

// ============================================================================
// FALLBACK ERROR SUPPRESSION
// In case any ResizeObserver errors still slip through (e.g., from iframes
// or third-party scripts), we suppress them here.
// ============================================================================

// Suppress at the error event level (capture phase, runs before React overlay)
window.addEventListener(
  "error",
  (event) => {
    if (
      event.message?.includes?.("ResizeObserver") ||
      event.error?.message?.includes?.("ResizeObserver")
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return true;
    }
  },
  true
);

// Suppress console.error for ResizeObserver (cleaner dev console)
const originalConsoleError = console.error;
console.error = (...args) => {
  const msg = args[0];
  if (
    (typeof msg === "string" && msg.includes("ResizeObserver")) ||
    (msg?.message?.includes?.("ResizeObserver"))
  ) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// Additional suppression for React's error overlay in development
// This catches errors that bypass the window.addEventListener above
if (process.env.NODE_ENV === 'development') {
  // Override React's error handling for the overlay
  const originalError = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (
      typeof message === 'string' &&
      message.includes('ResizeObserver')
    ) {
      return true; // Suppress the error
    }
    // Call original handler if it exists
    if (originalError) {
      return originalError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Also override unhandledrejection for completeness
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason?.message?.includes?.('ResizeObserver') ||
      event.reason?.toString?.()?.includes?.('ResizeObserver')
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
}

// ============================================================================
// APPLICATION IMPORTS & SETUP
// ============================================================================
import { StrictMode } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router } from "react-router-dom";
import Layout from "./Layout";
import { AuthProvider } from "./components/auth/AuthContext";
import { LanguageProvider } from "./components/language/LanguageContext";
import { ScopeProvider } from "./components/scope/ScopeContext";
import AuthWrapper from "./components/auth/AuthWrapper";
import "./firebase/firebase"; // Initialize Firebase

const rootElement = document.getElementById("root");

// App component wrapped in all providers
const App = (
  <StrictMode>
    <Router>
      <AuthProvider>
        <LanguageProvider>
          <ScopeProvider>
            <AuthWrapper>
              <Layout />
            </AuthWrapper>
          </ScopeProvider>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  </StrictMode>
);

// Hide static fallback when React loads
const hideStaticFallback = () => {
  const fallback = document.getElementById('static-fallback');
  if (fallback) {
    fallback.style.display = 'none';
    fallback.setAttribute('data-react-loaded', 'true');
  }
};

// Use hydrate if content was pre-rendered (react-snap), otherwise use render
// This enables SEO-friendly pre-rendering while maintaining full interactivity
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrate(App, rootElement);
  hideStaticFallback();
} else {
  ReactDOM.render(App, rootElement);
  // Hide fallback after React renders
  setTimeout(hideStaticFallback, 100);
}
